/**
 * Scanner (/scanner) — point d'entrée caméra unique. Détecte automatiquement le type de
 * document et ouvre le parcours adapté (étiquette, facture, rafale). L'IA pré-remplit, l'humain valide.
 */
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Wine, Grape } from 'lucide-react';
import { scannerApi } from '../../services/api';
import { Tag } from '../../components/ui/kit';
import CameraView from './CameraView';
import LabelFlow from './LabelFlow';
import InvoiceFlow from './InvoiceFlow';
import BurstMode from './BurstMode';
import { DOC_TYPE_LABELS } from '../../constants';

export default function Scanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState('camera');
  const [burst, setBurst] = useState(false);
  const [enrich, setEnrich] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(null);
  const [captured, setCaptured] = useState(null);
  // Présélection éventuelle depuis « Noter un vin » (ouvre directement le choix des photos en mode dégusté).
  const [scanMode, setScanMode] = useState(location.state?.mode === 'deguste' ? 'deguste' : null); // 'cave' | 'deguste'

  const reset = () => { setMode('camera'); setCaptured(null); setDetected(null); };

  const onFirstCapture = async (b64, preview) => {
    setDetecting(true);
    try {
      const { type } = await scannerApi.detect(b64);
      setDetected(type);
      setCaptured({ images: [b64], previews: [preview] });
      if (type === 'facture' || type === 'bon_de_livraison' || type === 'ticket') setMode('invoice');
      else if (type === 'qr_casier') { toast('QR casier détecté — ouverture de la sortie'); navigate('/sortie'); }
      else setMode('label');
    } catch {
      toast.error("Échec de l'analyse — réessayez");
    } finally {
      setDetecting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 pb-[150px] md:pb-[150px] max-w-5xl overflow-x-hidden">
      {/* Choix de session : bouteille à ajouter ou vin dégusté */}
      {!scanMode ? (
        <div className="max-w-xl mx-auto mt-6" data-testid="scan-mode-choice">
          <h2 className="font-title text-3xl mb-2">Que souhaitez-vous scanner ?</h2>
          <p className="text-[13px] text-n600 mb-5">Ce choix s'applique à toute la session de scan.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <button onClick={() => setScanMode('cave')} data-testid="scan-mode-cave"
              className="vd-card p-5 text-left hover:border-gold transition-colors">
              <Wine className="w-6 h-6 text-gold700 mb-2" strokeWidth={1.5} />
              <p className="font-title text-xl">Bouteille à ajouter</p>
              <p className="text-[13px] text-n600 mt-1">Entrée en stock dans la cave.</p>
            </button>
            <button onClick={() => setScanMode('deguste')} data-testid="scan-mode-deguste"
              className="vd-card p-5 text-left hover:border-gold transition-colors">
              <Grape className="w-6 h-6 text-gold700 mb-2" strokeWidth={1.5} />
              <p className="font-title text-xl">Vin dégusté</p>
              <p className="text-[13px] text-n600 mt-1">Catalogué comme dégusté, sans entrée en stock.</p>
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* En-tête : détection + réglages */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-[13px]">
          <span className="text-n600">Détection automatique :</span>
          {detected ? <Tag tone="fill" data-testid="detected-type">{DOC_TYPE_LABELS[detected]}</Tag> : <span className="text-n500">—</span>}
          <Tag tone={scanMode === 'deguste' ? 'accent' : 'neutral'} data-testid="scan-mode-badge">{scanMode === 'deguste' ? 'Vin dégusté' : 'Ajout cave'}</Tag>
          <button onClick={() => setScanMode((m) => (m === 'deguste' ? 'cave' : 'deguste'))} className="vd-link text-[12px]" data-testid="scan-mode-change">changer</button>
        </div>
        {mode === 'camera' && !burst && (
          <div className="flex flex-wrap items-center gap-4 text-[13px]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} className="accent-[#b68235] w-4 h-4" data-testid="enrich-toggle" />
              Chercher aussi le site du domaine et le logo
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={burst} onChange={(e) => setBurst(e.target.checked)} className="accent-[#b68235] w-4 h-4" data-testid="burst-toggle" />
              Mode rafale
            </label>
          </div>
        )}
      </div>

      {mode === 'camera' && burst && <BurstMode onExit={() => { setBurst(false); reset(); }} />}

      {mode === 'camera' && !burst && (
        <div className="max-w-xl">
          <CameraView onResult={onFirstCapture} />
          {detecting && <p className="text-[13px] text-n600 mt-3 flex items-center gap-2" data-testid="detecting"><Loader2 className="w-4 h-4 animate-spin" /> Détection du type de document…</p>}
        </div>
      )}

      {mode === 'label' && captured && (
        <LabelFlow initialImages={captured.images} initialPreviews={captured.previews} enrich={enrich}
          tasteOnly={scanMode === 'deguste'} onCancel={reset} onScanNext={reset} />
      )}

      {mode === 'invoice' && captured && (
        <InvoiceFlow initialImage={captured.images[0]} preview={captured.previews[0]} onCancel={reset} />
      )}
      </>
      )}
    </div>
  );
}
